/* Platelux theme — all front-end interactivity, no framework.
   Handles: 3D plate tilt, mobile menu, search overlay, AJAX cart + drawer,
   quantity steppers, wishlist (localStorage), toasts, sticky header. */
(function () {
  'use strict';

  const PX = window.Platelux || {};
  const routes = PX.routes || {};

  /* ---------------------------------------------------------------- utils */
  const $ = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));

  function formatMoney(cents) {
    const amount = (cents / 100).toFixed(2).replace(/\.00$/, '');
    const fmt = PX.moneyFormat || '£{{amount}}';
    // Shopify money formats use {{amount}} / {{amount_no_decimals}} placeholders.
    if (fmt.indexOf('{{') !== -1) {
      return fmt
        .replace(/\{\{\s*amount\s*\}\}/, (cents / 100).toFixed(2))
        .replace(/\{\{\s*amount_no_decimals\s*\}\}/, Math.round(cents / 100))
        .replace(/\{\{[^}]+\}\}/g, amount);
    }
    return '£' + amount;
  }

  /* -------------------------------------------------------------- toasts */
  function toast(message) {
    const host = $('#ToastHost');
    if (!host) return;
    const el = document.createElement('div');
    el.className =
      'pointer-events-auto flex w-full max-w-sm items-center gap-3 rounded-xl border border-white/10 bg-ink-700/95 px-4 py-3 shadow-plate backdrop-blur-md animate-fade-in';
    el.innerHTML =
      '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#ECEAE4" stroke-width="1.7" class="shrink-0"><circle cx="12" cy="12" r="9"/><path d="m8.5 12 2.4 2.4L16 9.5"/></svg>' +
      '<span class="flex-1 text-sm">' + message + '</span>';
    host.appendChild(el);
    setTimeout(() => {
      el.style.transition = 'opacity .3s';
      el.style.opacity = '0';
      setTimeout(() => el.remove(), 300);
    }, 2800);
  }
  window.plateluxToast = toast;
  // Exposed so sections that render cards after load can wire up the plate swivel.
  window.plateluxInitTilt = initTilt;

  /* ------------------------------------------------------ sticky header */
  function initHeader() {
    const header = $('[data-header]');
    if (!header) return;
    const isHome = document.body.dataset.template === 'index';
    const apply = () => {
      const solid = window.scrollY > 24 || !isHome;
      header.classList.toggle('is-solid', solid);
    };
    apply();
    window.addEventListener('scroll', apply, { passive: true });
  }

  /* -------------------------------------------------------- mobile menu */
  function initMobileMenu() {
    const toggle = $('[data-menu-toggle]');
    const menu = $('[data-mobile-menu]');
    if (!toggle || !menu) return;
    toggle.addEventListener('click', () => {
      const open = menu.classList.toggle('is-open');
      toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
    });
  }

  /* ----------------------------------------------------- 3D plate tilt */
  function initTilt() {
    if (document.documentElement.dataset.tilt === 'off') return;
    if (window.matchMedia('(hover: none)').matches) return;
    $$('[data-tilt]').forEach((el) => {
      // Cards injected later (e.g. "You may also like") re-run this, so skip anything
      // already wired up rather than stacking a second set of listeners on it.
      if (el.dataset.tiltBound) return;
      el.dataset.tiltBound = '1';
      const max = parseFloat(el.dataset.tilt) || 9;
      const glare = el.querySelector('[data-glare]');
      el.addEventListener('mousemove', (e) => {
        if (el.hasAttribute('data-tilt-disabled')) return;
        const r = el.getBoundingClientRect();
        const px = (e.clientX - r.left) / r.width;
        const py = (e.clientY - r.top) / r.height;
        el.style.transform =
          'perspective(1000px) rotateX(' + ((0.5 - py) * max * 2).toFixed(2) +
          'deg) rotateY(' + ((px - 0.5) * max * 2).toFixed(2) + 'deg) translateZ(12px) scale(1.02)';
        if (glare) {
          glare.style.opacity = '1';
          glare.style.background =
            'radial-gradient(circle at ' + (px * 100) + '% ' + (py * 100) +
            '%, rgba(255,255,255,0.28), rgba(255,255,255,0.05) 35%, transparent 60%)';
        }
      });
      el.addEventListener('mouseleave', () => {
        el.style.transform = 'perspective(1000px) rotateX(0) rotateY(0) translateZ(0) scale(1)';
        if (glare) glare.style.opacity = '0';
      });
    });
  }

  /* ------------------------------------------------- quantity steppers */
  function initQuantitySteppers() {
    document.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-qty-btn]');
      if (!btn) return;
      const wrap = btn.closest('[data-qty]');
      const input = $('input', wrap);
      const step = btn.dataset.qtyBtn === 'inc' ? 1 : -1;
      const min = parseInt(input.min || '1', 10);
      const next = Math.max(min, (parseInt(input.value, 10) || min) + step);
      input.value = next;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  /* ------------------------------------------------------------- cart */
  const cartDrawer = {
    el: null,
    open() {
      this.el = this.el || $('[data-cart-drawer]');
      if (!this.el) return;
      this.el.classList.add('is-open');
      this.el.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
    },
    close() {
      if (!this.el) this.el = $('[data-cart-drawer]');
      if (!this.el) return;
      this.el.classList.remove('is-open');
      this.el.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
    },
  };

  async function fetchCart() {
    const res = await fetch(routes.cart_url + '.js', { headers: { Accept: 'application/json' } });
    return res.json();
  }

  // The plate name is no longer printed in the drawer, but it still has to reach
  // a screen reader through the link's aria-label - so it needs escaping.
  function escAttr(v) {
    return String(v == null ? '' : v).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
  }

  function renderCart(cart) {
    // Update count badges
    $$('[data-cart-count]').forEach((n) => {
      n.textContent = cart.item_count;
      n.classList.toggle('hidden', cart.item_count === 0);
    });
    $$('[data-cart-total]').forEach((n) => (n.textContent = formatMoney(cart.total_price)));

    const body = $('[data-cart-body]');
    if (!body) return;

    if (cart.item_count === 0) {
      body.innerHTML =
        '<div class="flex flex-1 flex-col items-center justify-center gap-4 px-6 py-20 text-center">' +
        '<p class="font-semibold">Your cart is empty</p>' +
        '<a href="' + (routes.cart_url ? '/collections/all' : '#') + '" class="btn-primary">Browse the Collection</a>' +
        '</div>';
      const foot = $('[data-cart-footer]');
      if (foot) foot.classList.add('hidden');
      return;
    }

    const foot = $('[data-cart-footer]');
    if (foot) foot.classList.remove('hidden');

    body.innerHTML = cart.items
      .map((item) => {
        // Plates are listed by the size chosen, since the artwork is already in the picture
        // beside it. An accessory has no genre on it and often one nameless variant, so
        // showing its option would read "Default Title" - those are listed by name.
        const optionValues = (item.options_with_values || [])
          .map((o) => o.value)
          .join(' · ');
        const listBySize =
          !!(item.product_type && String(item.product_type).trim()) &&
          !item.product_has_only_default_variant &&
          optionValues && optionValues !== 'Default Title';
        const props = listBySize ? optionValues : item.product_title;
        return (
          '<li class="flex gap-4 py-4">' +
          '<a href="' + item.url + '" aria-label="' + escAttr(item.product_title) + '"><img src="' +
          (item.image ? item.image.replace(/(\.[^.]*)$/, '_160x$1') : '') +
          '" alt="" class="h-24 w-[72px] rounded-lg border border-white/10 object-cover"></a>' +
          '<div class="flex min-w-0 flex-1 flex-col">' +
          '<div class="flex items-start justify-between gap-2">' +
          '<div class="min-w-0"><a href="' + item.url + '" aria-label="' + escAttr(item.product_title) +
          '" class="truncate font-medium text-cream hover:text-gold-500">' + escAttr(props) + '</a></div>' +
          '<button data-cart-remove="' + item.key + '" aria-label="Remove" class="shrink-0 text-cream/40 hover:text-red-400">' +
          '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7"><path d="M4 7h16M9 7V5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2M6 7l1 12a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1l1-12"/></svg>' +
          '</button></div>' +
          '<div class="mt-auto flex items-center justify-between pt-2">' +
          '<div data-qty class="inline-flex items-center rounded-full border border-white/15 bg-ink-700">' +
          '<button data-qty-btn="dec" data-cart-qty="' + item.key + '" class="grid h-8 w-8 place-items-center text-cream/80 hover:text-gold-500" aria-label="Decrease">−</button>' +
          '<span class="w-9 text-center text-sm font-semibold">' + item.quantity + '</span>' +
          '<button data-qty-btn="inc" data-cart-qty="' + item.key + '" class="grid h-8 w-8 place-items-center text-cream/80 hover:text-gold-500" aria-label="Increase">+</button>' +
          '</div>' +
          '<span class="font-semibold">' + formatMoney(item.final_line_price) + '</span>' +
          '</div></div></li>'
        );
      })
      .join('');

    // Free shipping progress
    const threshold = parseInt(body.dataset.threshold || '0', 10) * 100;
    const bar = $('[data-ship-bar]');
    const msg = $('[data-ship-msg]');
    if (threshold > 0 && bar && msg) {
      const remaining = Math.max(0, threshold - cart.total_price);
      bar.style.width = Math.min(100, (cart.total_price / threshold) * 100) + '%';
      msg.innerHTML =
        remaining > 0
          ? 'Add <span class="font-semibold text-gold-500">' + formatMoney(remaining) + '</span> more for free shipping'
          : '🎉 You’ve unlocked free shipping!';
    }
  }

  async function addToCart(form) {
    const formData = new FormData(form);
    const res = await fetch(routes.cart_add_url + '.js', {
      method: 'POST',
      headers: { Accept: 'application/json' },
      body: formData,
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      toast(err.description || 'Could not add to cart');
      return;
    }
    const added = await res.json().catch(() => null);
    const cart = await fetchCart();
    renderCart(cart);
    cartDrawer.open();
    toast(PX.strings ? PX.strings.addedToCart : 'Added to cart');
    // Announced so the accessory offer can decide whether this add is worth an offer.
    // A multi-item add comes back as { items: [...] }, a single one as the line itself.
    if (added) {
      const items = added.items || [added];
      document.dispatchEvent(new CustomEvent('platelux:added', { detail: { items: items, cart: cart } }));
    }
  }

  // Lets other bits of the site put something in the basket and have the drawer catch up.
  window.plateluxRefreshCart = function () { return fetchCart().then(renderCart); };
  window.plateluxOpenCart = function () { return fetchCart().then(renderCart).then(() => cartDrawer.open()); };

  async function changeLine(key, quantity) {
    const res = await fetch(routes.cart_change_url + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity }),
    });
    const cart = await res.json();
    renderCart(cart);
    // If we're on the full cart page, reload its line items.
    if (document.body.dataset.template === 'cart') window.location.reload();
  }

  function initCart() {
    // Add-to-cart forms (product page + quick add)
    document.addEventListener('submit', (e) => {
      const form = e.target.closest('form[data-product-form]');
      if (!form) return;
      e.preventDefault();
      addToCart(form);
    });

    // Open/close drawer
    document.addEventListener('click', (e) => {
      if (e.target.closest('[data-cart-open]')) {
        e.preventDefault();
        fetchCart().then(renderCart);
        cartDrawer.open();
      }
      if (e.target.closest('[data-cart-close]')) cartDrawer.close();
      const remove = e.target.closest('[data-cart-remove]');
      if (remove) changeLine(remove.dataset.cartRemove, 0);
      const qtyBtn = e.target.closest('[data-cart-qty]');
      if (qtyBtn) {
        const key = qtyBtn.dataset.cartQty;
        const span = qtyBtn.parentElement.querySelector('span');
        const current = parseInt(span.textContent, 10);
        const next = qtyBtn.dataset.qtyBtn === 'inc' ? current + 1 : current - 1;
        changeLine(key, Math.max(0, next));
      }
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') cartDrawer.close();
    });

    // Prime the badge on load.
    fetchCart().then(renderCart).catch(() => {});
  }

  /* ---------------------------------------------------- search overlay */
  function initSearch() {
    const overlay = $('[data-search-overlay]');
    if (!overlay) return;
    const input = $('input[type="search"]', overlay);
    const results = $('[data-search-results]', overlay);
    let timer;

    const open = () => {
      overlay.classList.add('is-open');
      document.body.style.overflow = 'hidden';
      setTimeout(() => input && input.focus(), 50);
    };
    const close = () => {
      overlay.classList.remove('is-open');
      document.body.style.overflow = '';
    };

    document.addEventListener('click', (e) => {
      if (e.target.closest('[data-search-open]')) {
        e.preventDefault();
        open();
      }
      if (e.target.closest('[data-search-close]') || e.target === overlay) close();
    });
    document.addEventListener('keydown', (e) => e.key === 'Escape' && close());

    if (input && results && routes.predictive_search_url) {
      input.addEventListener('input', () => {
        clearTimeout(timer);
        const q = input.value.trim();
        if (!q) {
          results.innerHTML = '';
          return;
        }
        timer = setTimeout(async () => {
          const url =
            routes.predictive_search_url +
            '?q=' + encodeURIComponent(q) +
            '&resources[type]=product&resources[limit]=8' +
            // Tags are not searched by default - without this, keyword tags never match.
            '&resources[options][fields]=title,product_type,variants.title,vendor,tag' +
            '&section_id=predictive-search';
          try {
            const res = await fetch(url);
            const text = await res.text();
            const doc = new DOMParser().parseFromString(text, 'text/html');
            const inner = doc.querySelector('[data-predictive-inner]');
            results.innerHTML = inner ? inner.innerHTML : '';
          } catch (err) {
            /* ignore */
          }
        }, 200);
      });
    }
  }

  /* --------------------------------------------------------- wishlist */
  const WISH_KEY = 'platelux.wishlist';
  function getWish() {
    try {
      return JSON.parse(localStorage.getItem(WISH_KEY) || '[]');
    } catch (e) {
      return [];
    }
  }
  function setWish(ids) {
    localStorage.setItem(WISH_KEY, JSON.stringify(ids));
  }
  function syncWishUI() {
    const ids = getWish();
    $$('[data-wish]').forEach((btn) => {
      btn.classList.toggle('is-active', ids.indexOf(btn.dataset.wish) !== -1);
    });
    $$('[data-wish-count]').forEach((n) => {
      n.textContent = ids.length;
      n.classList.toggle('hidden', ids.length === 0);
    });
  }
  function initWishlist() {
    document.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-wish]');
      if (!btn) return;
      e.preventDefault();
      const id = btn.dataset.wish;
      const ids = getWish();
      const i = ids.indexOf(id);
      if (i === -1) {
        ids.push(id);
        toast(PX.strings ? PX.strings.savedToWishlist : 'Saved to wishlist');
      } else {
        ids.splice(i, 1);
        toast(PX.strings ? PX.strings.removedFromWishlist : 'Removed from wishlist');
      }
      setWish(ids);
      syncWishUI();
    });
    syncWishUI();
  }

  /* ------------------------------------------------------- accordions */
  function initAccordions() {
    document.addEventListener('click', (e) => {
      const toggle = e.target.closest('.accordion__toggle');
      if (!toggle) return;
      const item = toggle.closest('.accordion__item');
      if (item) item.classList.toggle('is-open');
    });
  }

  /* --------------------------------------------------- scroll reveal */
  // Fade + rise each section as it scrolls into view. Sections already in the
  // viewport on load are left untouched (no flash); only off-screen ones get
  // the hidden initial state, so there's no FOUC if this never runs.
  function initReveal() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    if (!('IntersectionObserver' in window)) return;
    const sections = $$('main section, main > div > section');
    if (!sections.length) return;
    const io = new IntersectionObserver((entries) => {
      entries.forEach((en) => {
        if (en.isIntersecting) {
          en.target.classList.add('is-in');
          io.unobserve(en.target);
        }
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });
    const vh = window.innerHeight || document.documentElement.clientHeight;
    sections.forEach((s) => {
      if (s.dataset.reveal === 'off') return;
      // Only reveal-animate sections that start below the fold.
      if (s.getBoundingClientRect().top > vh * 0.85) {
        s.classList.add('reveal-init');
        io.observe(s);
      }
    });
  }

  /* ----------------------------------------------------- init on load */
  // Genre pills sit on the artwork. On a pale plate the glass pill and its white text
  // disappear, so each one reads the corner of its own picture and, if that corner is
  // light, swaps to a solid dark pill. A 48px copy is fetched for sampling - the CDN
  // sends CORS headers, so the canvas stays readable - and the displayed image is
  // untouched.
  function initGenreChips() {
    var chips = document.querySelectorAll('[data-genre-chip][data-sample]');
    if (!chips.length || !window.HTMLCanvasElement) return;
    var canvas = document.createElement('canvas');
    var ctx = canvas.getContext && canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) return;

    function measure(chip) {
      if (chip.dataset.checked) return;
      chip.dataset.checked = '1';
      var img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = function () {
        try {
          var w = 24, h = 16;                       // the corner the pill covers
          canvas.width = w; canvas.height = h;
          ctx.clearRect(0, 0, w, h);
          ctx.drawImage(img, 0, 0, Math.max(1, Math.round(img.width * 0.55)),
                             Math.max(1, Math.round(img.height * 0.28)), 0, 0, w, h);
          var d = ctx.getImageData(0, 0, w, h).data, sum = 0, n = 0;
          for (var i = 0; i < d.length; i += 4) {
            // Rec. 601 luma, the same weighting the eye gives each channel
            sum += (0.299 * d[i] + 0.587 * d[i + 1] + 0.114 * d[i + 2]) / 255;
            n++;
          }
          if (n && (sum / n) > 0.62) chip.classList.add('is-on-light');
        } catch (e) { /* a tainted canvas just leaves the pill as it was */ }
      };
      img.src = chip.getAttribute('data-sample');
    }

    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (en) {
          if (!en.isIntersecting) return;
          measure(en.target);
          io.unobserve(en.target);
        });
      }, { rootMargin: '200px' });
      chips.forEach(function (c) { io.observe(c); });
    } else {
      chips.forEach(measure);
    }
  }
  window.plateluxInitGenreChips = initGenreChips;

  function init() {
    initHeader();
    initMobileMenu();
    initTilt();
    initQuantitySteppers();
    initCart();
    initSearch();
    initWishlist();
    initAccordions();
    initReveal();
    initGenreChips();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-init tilt + reveal when sections are re-rendered in the theme editor.
  document.addEventListener('shopify:section:load', (e) => {
    initTilt();
    // Reveal the just-loaded section immediately so editing never hides it.
    const sec = e.target.querySelector('section') || e.target;
    if (sec && sec.classList) sec.classList.add('is-in');
  });
})();
